import { useEffect, useState } from "react";

/**
 * Animuje liczbę od 0 do `target` w ciągu `durationMs` z easing
 * `cubic-bezier(0.16, 1, 0.3, 1)` (overshoot + settle).
 *
 * Używa requestAnimationFrame — bez bibliotek. Respektuje
 * prefers-reduced-motion: zwraca target od razu.
 */
export function useAnimatedNumber(
  target: number,
  durationMs: number = 600,
): number {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduceMotion) {
      setValue(target);
      return;
    }

    let raf = 0;
    const start = performance.now();
    const from = 0;

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // cubic-bezier(0.16, 1, 0.3, 1) approximation (smooth out)
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(from + (target - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return value;
}
